import { getMoleculeDrawing, type StoredMoleculeDrawing } from '@peruselab/api-client/moleculeDrawings';
import { getIsomer } from '@peruselab/api-client/isomers';
import { validateChemicalDocument } from '@peruselab/structure-editor/chemistry/chemicalValidation';
import { reconstructDrawingDocument } from '@peruselab/structure-editor/domain/moleculeDrawingProjection';
import { fetchMolecule } from '../../components/moleculeCache';

export interface PublicDrawing {
  readonly drawing: StoredMoleculeDrawing;
  /** RDKit's canonical SMILES of the drawing's structure; null if it can't be computed. */
  readonly smiles: string | null;
  /** ADR 0017 (#904): the drawing's Molecule, for the link to its page; null if unknown. */
  readonly moleculeId: string | null;
  /** #1123: its formula, for the page's head when the drawing has no title; null if unknown. */
  readonly formula: string | null;
  /** ADR 0026 Q1 (#1162): it has a hapto bond, which SMILES can't express -- `smiles` are its pieces. */
  readonly haptoBonds: boolean;
}

/**
 * #885: a public drawing and its SMILES, through the same reads a thumbnail uses (drawing ->
 * isomer -> molecule), all open to signed-out visitors for public drawings. SMILES is computed in
 * the browser by RDKit, exactly as the editor's validation panel shows it.
 */
export async function loadPublicDrawing(drawingId: string): Promise<PublicDrawing> {
  const drawing = await getMoleculeDrawing(drawingId);
  const [ref] = drawing.document.isomerRefs;
  if (!ref) return { drawing, smiles: null, moleculeId: null, formula: null, haptoBonds: false };
  let moleculeId: string | null = null;
  try {
    const isomer = await getIsomer(ref.isomerId);
    moleculeId = isomer.moleculeId;
    const molecule = await fetchMolecule(isomer.moleculeId);
    const document = reconstructDrawingDocument(drawing.document, molecule.document, isomer.document);
    const validation = await validateChemicalDocument(document);
    return {
      drawing,
      smiles: validation.properties?.canonicalSmiles ?? null,
      moleculeId,
      formula: validation.properties?.molecularFormula ?? null,
      haptoBonds: Object.keys(molecule.document.haptoBonds ?? {}).length > 0
    };
  } catch {
    return { drawing, smiles: null, moleculeId, formula: null, haptoBonds: false };
  }
}
